// src/pages/admin/DirectRegisterPanel.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";

import { directRegister, listMarkets, resendCredentials } from "../../api/adminService";

const EMPTY_FORM = {
  email: "",
  full_name: "",
  market_id: "",
  org_name: "",
  admin_notes: "",
};

export default function DirectRegisterPanel() {
  const [markets, setMarkets] = useState([]);
  const [marketsLoading, setMarketsLoading] = useState(true);
  const [marketsError, setMarketsError] = useState("");

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  // Resend credentials dialog
  const [resendTarget, setResendTarget] = useState(null); // { user_id, email }
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState("");
  const [resendDone, setResendDone] = useState(false);

  const handleOpenResend = (user) => {
    setResendTarget(user);
    setResendError("");
    setResendDone(false);
  };

  const handleCloseResend = () => {
    if (resendLoading) return;
    setResendTarget(null);
  };

  const handleResendCredentials = async () => {
    setResendLoading(true);
    setResendError("");
    try {
      await resendCredentials(resendTarget.user_id);
      setResendDone(true);
    } catch (e) {
      setResendError(e.message);
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    setMarketsLoading(true);
    listMarkets()
      .then((data) => setMarkets((data.markets || data).filter((m) => m.status !== "closed")))
      .catch((e) => setMarketsError(e.message))
      .finally(() => setMarketsLoading(false));
  }, []);

  const selectedMarket = markets.find((m) => m.id === form.market_id) || null;

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMarket) return;
    setLoading(true);
    setError("");
    setSuccess(null);
    try {
      const result = await directRegister({
        email: form.email.trim(),
        full_name: form.full_name.trim(),
        city: selectedMarket.city,
        state: selectedMarket.state,
        org_name: form.org_name.trim() || undefined,
        admin_notes: form.admin_notes.trim() || undefined,
      });
      setSuccess(result);
      setForm(EMPTY_FORM);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const isValid =
    form.email.trim() &&
    form.full_name.trim() &&
    form.market_id;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <PersonAddRoundedIcon color="primary" sx={{ fontSize: 28 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Direct Register
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create an organization and user account instantly — no access request or invite needed.
          </Typography>
        </Box>
      </Box>

      {success && (
        <Alert
          severity="success"
          icon={<CheckCircleRoundedIcon />}
          sx={{ mb: 3 }}
          action={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Resend credentials">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleOpenResend({ user_id: success.user_id, email: success.email })}
                >
                  <MarkEmailReadRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button size="small" onClick={() => setSuccess(null)}>
                Register another
              </Button>
            </Stack>
          }
        >
          <Typography variant="body2" fontWeight={600}>
            Account created — credentials sent to{" "}
            <strong>{success.email}</strong>
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} flexWrap="wrap">
            <Chip label={`Org: ${success.org_name}`} size="small" color="primary" variant="outlined" />
            <Chip label={`${success.market_city}, ${success.market_state}`} size="small" variant="outlined" />
            <Chip label={`User ID: ${success.user_id}`} size="small" variant="outlined" />
          </Stack>
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, maxWidth: 560 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
          Account details
        </Typography>

        {marketsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not load markets: {marketsError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            <TextField
              label="Full name"
              placeholder="John Doe"
              value={form.full_name}
              onChange={handleChange("full_name")}
              required
              fullWidth
              size="small"
              disabled={loading}
            />

            <TextField
              label="Email"
              type="email"
              placeholder="agent@brokerage.com"
              value={form.email}
              onChange={handleChange("email")}
              required
              fullWidth
              size="small"
              disabled={loading}
            />

            <TextField
              select
              label="Market (city / state)"
              value={form.market_id}
              onChange={handleChange("market_id")}
              required
              fullWidth
              size="small"
              disabled={loading || marketsLoading}
              InputLabelProps={{ shrink: true }}
              helperText={
                marketsLoading
                  ? "Loading markets…"
                  : "Only open markets with available capacity are shown"
              }
            >
              {markets.length === 0 && !marketsLoading ? (
                <MenuItem disabled value="">
                  No open markets available
                </MenuItem>
              ) : (
                markets.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.city}, {m.state}
                    {m.capacity != null && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 1, color: "text.secondary" }}
                      >
                        ({m.capacity - (m.member_count ?? 0)} seats left)
                      </Typography>
                    )}
                  </MenuItem>
                ))
              )}
            </TextField>

            <Divider>
              <Typography variant="caption" color="text.secondary">
                Optional
              </Typography>
            </Divider>

            <TextField
              label="Organization name"
              placeholder="Austin Premier Realty"
              value={form.org_name}
              onChange={handleChange("org_name")}
              fullWidth
              size="small"
              disabled={loading}
              helperText="Leave blank to use the email domain as org name"
            />

            <TextField
              label="Admin notes"
              placeholder="Referred by partner…"
              value={form.admin_notes}
              onChange={handleChange("admin_notes")}
              fullWidth
              size="small"
              multiline
              rows={2}
              disabled={loading}
            />

            {error && <Alert severity="error">{error}</Alert>}

            <Button
              type="submit"
              variant="contained"
              disabled={loading || !isValid || marketsLoading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PersonAddRoundedIcon />}
              sx={{ alignSelf: "flex-start" }}
            >
              {loading ? "Creating account…" : "Create account"}
            </Button>
          </Stack>
        </Box>
      </Paper>
      {/* Resend Credentials Dialog */}
      <Dialog
        open={Boolean(resendTarget)}
        onClose={handleCloseResend}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LockResetRoundedIcon color="primary" />
          Resend Credentials
        </DialogTitle>
        <DialogContent>
          {resendDone ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              New credentials sent to <strong>{resendTarget?.email}</strong>.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                This will:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                <Typography component="li" variant="body2">Generate a new random password</Typography>
                <Typography component="li" variant="body2">Invalidate the current password immediately</Typography>
                <Typography component="li" variant="body2">
                  Send login credentials to <strong>{resendTarget?.email}</strong>
                </Typography>
              </Box>
              {resendError && <Alert severity="error" sx={{ mt: 2 }}>{resendError}</Alert>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!resendDone ? (
            <>
              <Button onClick={handleCloseResend} disabled={resendLoading}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleResendCredentials}
                disabled={resendLoading}
                startIcon={resendLoading ? <CircularProgress size={14} color="inherit" /> : <MarkEmailReadRoundedIcon />}
              >
                {resendLoading ? "Sending…" : "Send credentials"}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseResend} variant="contained">Close</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
